import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type {
  ComplianceCalendarEntry,
  GapAnalysisEntry,
} from '@dsr/shared-types';
import { useComplianceCalendar, useComplianceGap } from '../api/queries/compliance';
import { ApiError } from '../api/client';
import { KpiCard, StatusBadge } from '../components/ui';

// Compliance command center. Two analytics queries drive the whole page:
//   gap-analysis  -> status summary cards + jurisdiction target grid
//   calendar      -> upcoming deadlines panel + critical alerts list
// Visual conventions mirror DashboardPage / CredentialExpiryPage: kpi-grid +
// kpi-card, dashboard-card sections, status badges, page-state for load/error.

const DAY_MS = 24 * 60 * 60 * 1000;
const UPCOMING_LIMIT = 15;
const CRITICAL_WINDOW_DAYS = 30;

const SERVICE_LINE_LABEL: Record<string, string> = {
  unarmed_guard: 'Unarmed Guard',
  armed_guard: 'Armed Guard',
  executive_protection: 'Executive Protection',
  private_investigation: 'Private Investigation',
};

function serviceLabel(sl: string): string {
  return SERVICE_LINE_LABEL[sl] ?? sl.replace(/_/g, ' ');
}

function fmtDate(epoch: number): string {
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function daysUntil(epoch: number, now: number): string {
  const days = Math.round((epoch - now) / DAY_MS);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Today';
  return `${days}d`;
}

// A target is "met" when nothing is missing or expiring, "gap" when something
// is outright missing, otherwise "partial" (only expiring-soon items).
type TileState = 'met' | 'partial' | 'gap';

function tileState(entry: GapAnalysisEntry): TileState {
  if (entry.missing.length > 0) return 'gap';
  if (entry.expiring_soon.length > 0) return 'partial';
  return 'met';
}

// Reuse the status-badge palette: active(green), pending(amber), expired(red).
// Returns the raw status suffix consumed by <StatusBadge status=…>.
function stateBadgeStatus(state: TileState): string {
  if (state === 'met') return 'active';
  if (state === 'partial') return 'pending';
  return 'expired';
}

function stateKpiTone(state: TileState): 'success' | 'warn' | 'danger' {
  if (state === 'met') return 'success';
  if (state === 'partial') return 'warn';
  return 'danger';
}

// Calendar severity -> status-badge status suffix.
function severityBadgeStatus(severity: ComplianceCalendarEntry['severity']): string {
  if (severity === 'license_critical') return 'expired';
  if (severity === 'standard') return 'pending';
  return 'info';
}

export function ComplianceDashboardPage(): JSX.Element {
  const gap = useComplianceGap();
  const calendar = useComplianceCalendar();
  const now = Date.now();

  const entries = useMemo<GapAnalysisEntry[]>(
    () => gap.data?.entries ?? [],
    [gap.data],
  );

  // Bucket each target by state for the summary cards.
  const summary = useMemo(() => {
    const c = { met: 0, partial: 0, gap: 0 };
    for (const e of entries) c[tileState(e)]++;
    return c;
  }, [entries]);

  const calEntries = useMemo<ComplianceCalendarEntry[]>(
    () => calendar.data?.entries ?? [],
    [calendar.data],
  );

  // Calendar arrives sorted ascending by due_at; the next N are "upcoming".
  const upcoming = useMemo(
    () => calEntries.slice(0, UPCOMING_LIMIT),
    [calEntries],
  );

  // Critical alerts: license_critical events landing within 30 days.
  const criticalAlerts = useMemo(() => {
    const cutoff = now + CRITICAL_WINDOW_DAYS * DAY_MS;
    return calEntries.filter(
      (e) => e.severity === 'license_critical' && e.due_at <= cutoff,
    );
  }, [calEntries, now]);

  const isLoading = gap.isLoading || calendar.isLoading;
  const isError = gap.isError || calendar.isError;
  const error = gap.error ?? calendar.error;

  return (
    <div className="page compliance-dashboard-page">
      <header className="page-header">
        <div>
          <h1>Compliance Dashboard</h1>
          <p className="page-sub">
            Darkspire Risk LLC regulatory standing across targeted jurisdictions
            — license, insurance, bond, and qualifying-agent coverage.
          </p>
        </div>
      </header>

      {isLoading && <div className="page-state">Loading compliance status…</div>}
      {isError && (
        <div className="page-state page-error" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load compliance data.'}
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <section className="kpi-grid">
            <KpiCard label="Targets met" value={summary.met} tone="success" />
            <KpiCard label="Partial coverage" value={summary.partial} tone="warn" />
            <KpiCard label="Coverage gap" value={summary.gap} tone="danger" />
            <KpiCard label="Total targets" value={entries.length} />
          </section>

          <section className="dashboard-card">
            <h2 className="card-title">Compliance modules</h2>
            <ul className="compliance-nav-list">
              <li><Link to="/compliance/gap">Gap Analysis</Link> — requirement-by-requirement coverage per target</li>
              <li><Link to="/compliance/licenses">Company Licenses</Link> — active and pending state licenses</li>
              <li><Link to="/compliance/insurance">Insurance &amp; Bonds</Link> — policies, certificates, and surety bonds</li>
              <li><Link to="/compliance/catalog">Requirements Catalog</Link> — browse jurisdiction requirements and reciprocity</li>
              <li><Link to="/compliance/roster">Roster Compliance</Link> — personnel on active assignments vs. employee-level requirements</li>
            </ul>
          </section>

          <section className="dashboard-card">
            <h2 className="card-title">Jurisdiction coverage</h2>
            {entries.length === 0 ? (
              <p className="page-state">No jurisdiction targets defined yet.</p>
            ) : (
              <div className="jurisdiction-grid">
                {entries.map((e) => {
                  const state = tileState(e);
                  return (
                    <div
                      key={`${e.jurisdiction.id}-${e.service_line}`}
                      className={`jurisdiction-tile tile-${stateKpiTone(state)}`}
                    >
                      <div className="tile-code">{e.jurisdiction.code}</div>
                      <div className="tile-service">{serviceLabel(e.service_line)}</div>
                      <div className="tile-counts">
                        <StatusBadge status={stateBadgeStatus(state)}>
                          {e.met.length} met
                        </StatusBadge>
                        {e.expiring_soon.length > 0 && (
                          <StatusBadge status="pending">
                            {e.expiring_soon.length} expiring
                          </StatusBadge>
                        )}
                        {e.missing.length > 0 && (
                          <StatusBadge status="expired">
                            {e.missing.length} missing
                          </StatusBadge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <div className="compliance-panels">
            <section className="dashboard-card">
              <h2 className="card-title">Upcoming deadlines</h2>
              {upcoming.length === 0 ? (
                <p className="page-state">No deadlines on the horizon. 🎉</p>
              ) : (
                <ul className="deadline-list">
                  {upcoming.map((e) => (
                    <li
                      key={`${e.ref_table}-${e.ref_id}-${e.kind}`}
                      className="deadline-row"
                    >
                      <span className="deadline-date">{fmtDate(e.due_at)}</span>
                      <span className="deadline-label">
                        {e.jurisdiction_code ? `${e.jurisdiction_code} · ` : ''}
                        {e.label}
                      </span>
                      <StatusBadge status={severityBadgeStatus(e.severity)}>
                        {daysUntil(e.due_at, now)}
                      </StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="dashboard-card">
              <h2 className="card-title">Critical alerts</h2>
              <p className="card-sub">
                License-critical events within {CRITICAL_WINDOW_DAYS} days.
              </p>
              {criticalAlerts.length === 0 ? (
                <p className="page-state">No critical alerts.</p>
              ) : (
                <ul className="alert-list">
                  {criticalAlerts.map((e) => (
                    <li
                      key={`${e.ref_table}-${e.ref_id}-${e.kind}`}
                      className="alert-row alert-danger"
                    >
                      <StatusBadge status="expired">
                        {daysUntil(e.due_at, now)}
                      </StatusBadge>
                      <span className="alert-label">
                        {e.jurisdiction_code ? `${e.jurisdiction_code} · ` : ''}
                        {e.label}
                      </span>
                      <span className="alert-date">{fmtDate(e.due_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
